/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Nikolay Sviridov
 * @version 1.30.2
 * @since 1.0.0
 */

import { ChangeEvent, FormEvent, useState } from 'react';

import { browserName, deviceType } from 'react-device-detect';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { deviceTypes } from '@pages/PageAuth/constants/authConstants';

import { getCaptchaIsOpen, getCaptchaTime, getCaptchaValue } from '../../../../modules/captcha/captchaSelectors';
import { closeNotificationAction, openNotification } from '../../../../modules/notification/notificationActions';
import {
    ENotificationLevelTypes,
    ENotificationTypes,
    INotification,
} from '../../../../modules/notification/types/notificationTypes';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { useAuthContext } from '../../PageAuthProvider';
import { getAuthState, makeSelectLoggedIn } from '../../selectors/authSelectors';
import { IAuthState, ILoginProcessRequest } from '../../types/authTypes';
import AuthForm from './AuthForm';

interface IAuthFormProps {
    isServiceAccess?: boolean;
}

export interface IAuthFormState {
    login: string;
    password: string;
    validForm: boolean;
}

export type TAuthFormContainer = IAuthFormProps & IConnectedProps & IDispatchProps;

export const AuthFormContainer = (props: TAuthFormContainer) => {

    const {
        authorization,
        captchaTime,
        captchaValue,
        isCaptchaShouldBeShown,
        // actions
        closeNotice,
        openNotice,
    } = props;
    const userAgent = browserName;
    const typeDevice = deviceTypes[deviceType] ?? deviceTypes.unknown;

    // get auth context
    const { translation, loginProcess } = useAuthContext();

    const [authForm, setAuthForm] = useState<IAuthFormState>({ login: '', password: '', validForm: true });

    const inputHandler = (event: ChangeEvent<HTMLInputElement>, field: string) => {
        setAuthForm({ ...authForm, [field]: event.target.value });
    };

    const submitHandler = (event: FormEvent): void => {
        event.preventDefault();
        if (authForm.login && authForm.password) {
            const loginForm: ILoginProcessRequest = {
                body: { captchaTime, password: authForm.password, username: authForm.login, userAgent, typeDevice },
                callback: () => setAuthForm({ ...authForm, validForm: false }),
            };
            if (isCaptchaShouldBeShown) {
                if (!captchaValue) {
                    openNotice({
                        message: translation.notice.captchaRequired,
                        type: ENotificationTypes.AUTH,
                        level: ENotificationLevelTypes.ERROR,
                    });
                    setAuthForm({ ...authForm, validForm: false });
                } else {
                    loginForm.body.captcha = captchaValue;
                    loginProcess(loginForm);
                    setAuthForm({ ...authForm, validForm: true });
                    closeNotice();
                }
            } else {
                loginProcess(loginForm);
                setAuthForm({ ...authForm, validForm: true });
                closeNotice();
            }
        } else {
            openNotice({
                message: translation.notice.errorLoginForm,
                type: ENotificationTypes.AUTH,
                level: ENotificationLevelTypes.ERROR,
            });
            setAuthForm({ ...authForm, validForm: false });
        }
    };

    return <AuthForm
        inputHandler={inputHandler}
        isCaptchaShouldBeShown={isCaptchaShouldBeShown}
        showCaptcha={authorization.isCaptchaShouldBeShown}
        submitHandler={submitHandler}
        validForm={authForm.validForm}
    />;
};

interface IConnectedProps {
    authorization: IAuthState;
    captchaTime?: number;
    captchaValue: string;
    isCaptchaShouldBeShown: boolean;
    loggedIn: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    authorization: (state: IRootReducer) => getAuthState(state),
    captchaTime: getCaptchaTime(),
    captchaValue: getCaptchaValue(),
    isCaptchaShouldBeShown: getCaptchaIsOpen(),
    loggedIn: makeSelectLoggedIn(),
});

interface IDispatchProps {
    closeNotice: () => void;
    openNotice: ({ message, type }: INotification) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    closeNotice: () => dispatch(closeNotificationAction()),
    openNotice: (payload: INotification) => dispatch(openNotification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthFormContainer);
